import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_IMAGE } from "@/lib/data";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
};

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HEAD = ["Da file STL", "a pezzi reali,", "senza stress."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen flex items-end tech-grid-bg overflow-hidden pt-28 pb-14"
    >
      {/* Parallax image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMAGE} alt="Stampante 3D in azione" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E14] via-[#0A0E14]/80 to-[#0A0E14]/40" />
        <div className="absolute inset-0 bg-[#0A0E14]/30" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-2 w-2 rounded-full bg-[#00C8D7] animate-pulse" />
          <span className="font-mono-tech text-xs uppercase tracking-[0.28em] text-[#00C8D7]">
            Stampa 3D Professionale — FDM / PLA
          </span>
        </motion.div>

        <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tighter max-w-5xl">
          {HEAD.map((t, i) => (
            <span key={i} className="mask-reveal">
              <motion.span
                variants={line}
                custom={i}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 2 ? (
                  <span className="text-[#F5871F]">{t}</span>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg text-slate-300 leading-relaxed"
        >
          Per aziende, professionisti e realtà locali che vogliono prototipi, piccoli lotti e
          soluzioni su misura, subito.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("quote")}
            className="group inline-flex items-center justify-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold px-8 py-4 hover:bg-[#00C8D7]/85 transition-colors"
          >
            Invia il tuo file STL
            <ArrowUpRight size={20} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("solution")}
            className="inline-flex items-center justify-center gap-2 border border-[#00C8D7] text-[#00C8D7] font-bold px-8 py-4 hover:bg-[#00C8D7]/10 transition-colors"
          >
            Scopri come funziona
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-16 flex items-center gap-2 text-slate-500"
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="font-mono-tech text-xs uppercase tracking-[0.2em]">Scorri</span>
        </motion.div>
      </div>
    </section>
  );
}
